import { useCallback, useMemo, useState } from 'react';
import {
  Color, FILE_NAME_FIELD, FILE_NAME_MAX_LENGTH, FILE_UNITS_PATH, FORM_DATE_FIELD, FORM_NAME_FIELD, FORM_NAME_MAX_LENGTH, standardFormFieldsProblem, standardFormTakesName, STANDARD_FORM_ENTITY_PATH, documentDateProblem, fill, isStandardForm, uploadFileNameProblem, uploadLineName, PHOTO_CHECK_LINE_KEYS, PHOTO_COACHING_PROMPT_KEYS, Size, UNIT_TAGS_FIELD, fileLinksPath, imageStandardOf, t, unitTagRequired, unitTagsOf,
  type DocumentAttachmentKind, type FileTypeRow, type PagedRecord, type PhotoCheck, type PhotoChecks, type QueuedFile, type StandardFormEntity, type StringKey, type UploadAct, type UploadFn, standardFormAsksDate, uploadActFileTypes,
  EMPLOYMENT_ANSWERS_FIELD, EMPLOYMENT_QUESTION_CONFIDENTIALITY, EMPLOYMENT_QUESTION_DATE_SIGNED, EMPLOYMENT_QUESTION_NON_SOLICIT, EMPLOYMENT_QUESTION_RETURN_OR_DESTROY, employmentAnswersProblem, employmentQuestionsFor, type EmploymentAnswers,
} from '@bms2/shared';
import { Button, DatePicker, NumericInput, Select, TextInput } from '../../components/controls';
import { FormFields } from '../../components/forms';
import { useOrganizationToday } from '../../shell/ViewerZone';
import { UploadFileModal } from '../../components/modals/UploadFileModal';
import { FILES_PATH, FILE_TYPES_PATH, post, upload, useApi, useRows } from './api';
import styles from './RecordUploadModal.module.css';
import { useDuplicateQuestion } from './DuplicateQuestion';
import { useSameNameQuestion } from './SameNameQuestion';

/**
 * A Files tile's ↑ Upload (piece 13): the Upload File modal of piece 5 over
 * the file types Settings holds, writing each file tagged to this record so it
 * shows in the tile it was uploaded from. 🔨 The file type is required and has
 * no default, the rule that modal already keeps. An upload whose type runs the
 * photo pipeline answers `AwaitingConfirm`, and the page opens Scan confirm on
 * it at once (decision 0345).
 */

export interface UploadedFile {
  id: string;
  filename: string;
  photoPipelineStatus?: string | null;
  /** 🔒 Decision 6380: a site photo's checks, answered with the upload while the person is still on site; null while its scan has not cleared it. */
  photoChecks?: PhotoChecks | null;
}

/** One landed photo's checks, as the window lists them. */
interface CheckedPhoto {
  id: string;
  filename: string;
  checks: PhotoChecks;
  removed: boolean;
}

export interface RecordUploadModalProps {
  open: boolean;
  recordType: PagedRecord | DocumentAttachmentKind;
  recordId: string;
  apiBaseUrl?: string;
  onCancel: () => void;
  onSaved: () => void;
  openerId?: string | null;
  /** Files dropped straight onto the Files tile (piece 13); the modal opens with them queued and asks the type first. */
  initialFiles?: readonly File[];
  /** Each file the server accepted, for a caller that points a record at it: a stage question answered by its upload (decision 0332). */
  onUploaded?: (file: { id: string; filename: string }) => Promise<void> | void;
  /** A file the pipeline holds for a person's confirm: the page opens Scan confirm on it (decision 0345). */
  onScanConfirm?: (file: { id: string; filename: string }) => void;
  /**
   * 🔒 Decision 5440: on a Deal, the kinds an existing-equipment photo takes. The window draws a Kind choice from
   * them, and a picture sent with one is an existing-equipment photo with no position. Absent, no choice is drawn.
   */
  photoKinds?: readonly string[];
  /** 🔒 Decision 6000 (row T2766): the kind the window opens with, a Discovery upload point's own; the choice stays open to change. */
  presetPhotoKind?: string;
  /**
   * 🔒 Decision 7850: the act that opened the window where it already says what the file is, a photo point's Take
   * Photo. The type list offers the act's own types, and Other Type reveals the rest that apply. Absent, as a Files
   * tile opens it, every type that applies is offered.
   */
  act?: UploadAct;
}

interface FileTypeAnswer {
  id: string;
  name: string;
  classification: string | null;
  isActive: boolean;
  appliesTo?: string[];
  runsPhotoPipeline?: boolean;
  /** Decision 2420: a type the application writes or assigns itself, which this picker leaves out. */
  isSystem?: boolean;
  /** 🔒 Decision 6380: the image standard, which makes a picture of the type a site photo naming its units on a Deal. */
  compressOnUpload?: boolean;
  longEdgePx?: number | null;
  jpegQuality?: number | null;
  /** 🔒 Decision 9680: an employment form, which asks the date signed and, for the agreement, its questions beside the file. */
  isEmploymentForm?: boolean;
}

/** 🔒 Decision 9680: a Yes or No answer as the Select holds it, empty while unanswered. */
const YES_NO_OPTIONS = (): { value: string; label: string }[] => [{ value: 'yes', label: t('nda.review.yes') }, { value: 'no', label: t('nda.review.no') }];
const yesNoValue = (answer: boolean | null | undefined): string => (answer === true ? 'yes' : answer === false ? 'no' : '');
const yesNoAnswer = (value: string): boolean | null => (value === 'yes' ? true : value === 'no' ? false : null);

/** The questions an upload of the chosen type asks beyond its date signed; none for a type that is not an employment form. */
function employmentQuestionsOf(row: FileTypeAnswer | null | undefined) {
  return row && row.isEmploymentForm === true ? employmentQuestionsFor(row.name, true).filter((q) => q.number !== EMPLOYMENT_QUESTION_DATE_SIGNED) : [];
}

/** 🔒 One check's line in the owner's words, the low resolution naming both long edges and a duplicate the earlier file. */
export function photoCheckLine(check: PhotoCheck, checked: Pick<PhotoChecks, 'longEdgePx' | 'standardLongEdgePx'>): string {
  const text = t(PHOTO_CHECK_LINE_KEYS[check.kind] as StringKey);
  if (check.kind === 'lowResolution') return fill(text, { pixels: String(checked.longEdgePx), standard: `${checked.standardLongEdgePx} px` });
  if (check.kind === 'duplicate') return fill(text, { name: check.duplicateOf?.filename ?? '' });
  return text;
}

/** 🔒 Decision 11301: what the person has typed on one file's line; a line nobody touched reads its defaults. */
interface LineDraft {
  /** The name typed, or null while the line still follows the name the rule writes. */
  name: string | null;
  date: string;
  /** 🔒 Decision 11562: the bank or issuer typed in the line's Form Name box; empty by default, and it joins the standard name after the type. */
  formName: string;
}

export function RecordUploadModal({ open, recordType, recordId, apiBaseUrl = '', onCancel, onSaved, openerId = null, initialFiles = [], onUploaded, onScanConfirm, photoKinds, presetPhotoKind, act }: RecordUploadModalProps) {
  const [photoKind, setPhotoKind] = useState<string>(presetPhotoKind ?? '');
  // 🔒 Decision 11301 (amending 9540): each file has its own line, a name and a date, today until changed; a photo act, and a type carrying a photo standard, keep the window they had.
  const today = useOrganizationToday();
  const perFile = act !== 'photo';
  const linesFor = useCallback((chosen: FileTypeAnswer | null): boolean => perFile && chosen !== null && imageStandardOf(chosen) === null, [perFile]);
  const [lines, setLines] = useState<Readonly<Record<string, LineDraft>>>({});
  // 🔒 Decision 9680: an Employment Agreement's answers, asked beside the file; none is chosen for the person.
  const [answers, setAnswers] = useState<EmploymentAnswers>({ confidentiality: null, nonSolicit: null, nonSolicitMonths: null, returnOrDestroy: null });
  // The drop zone names a failed file without its reason, so the agreement's missing answer is drawn beside its questions.
  const [answersNeeded, setAnswersNeeded] = useState<string | null>(null);
  /** 🔒 Decision 9640: the next upload starts from today and no name, whether the window closed by Done or by Cancel. */
  const resetForm = () => {
    setLines({});
    setAnswers({ confidentiality: null, nonSolicit: null, nonSolicitMonths: null, returnOrDestroy: null });
    setAnswersNeeded(null);
  };
  // 🔒 Decision 11301: the name a standard form's entity takes on this record, which a form's line fills its name from.
  const entityRead = useApi<{ entity: StandardFormEntity | null }>(open && perFile ? `${STANDARD_FORM_ENTITY_PATH}?${new URLSearchParams({ taggedRecordType: recordType, taggedRecordId: recordId }).toString()}` : null, apiBaseUrl);
  const entity = entityRead.state.kind === 'ready' ? (entityRead.state.data?.entity ?? null) : null;
  // 🔒 Decision 6382: on a Deal a site photo names the machines or units it shows, typed or picked from the Deal's own.
  const onDeal = recordType === 'Deal';
  const [unitText, setUnitText] = useState('');
  const [checked, setChecked] = useState<readonly CheckedPhoto[]>([]);
  const [refusal, setRefusal] = useState<string | null>(null);
  // The drop zone names a failed file without its reason, so the unit's refusal is drawn here, beside the field it asks for.
  const [unitNeeded, setUnitNeeded] = useState(false);
  const known = useApi<{ units: string[] }>(open && onDeal ? `${FILE_UNITS_PATH}?${new URLSearchParams({ taggedRecordType: recordType, taggedRecordId: recordId }).toString()}` : null, apiBaseUrl);
  const knownUnits = known.state.kind === 'ready' && Array.isArray(known.state.data?.units) ? known.state.data.units : [];
  const units = unitTagsOf(unitText);
  const types = useRows<FileTypeAnswer>(open ? FILE_TYPES_PATH : null, apiBaseUrl, { cache: true });
  const typeRows = useMemo(() => (types.state.kind === 'ready' ? types.state.data : []), [types.state]);
  // A type naming record types is offered on those alone; one naming none is offered everywhere (decision 0299). 🔒 An act offers its own first (decision 7850).
  const cutTypes = uploadActFileTypes(typeRows.filter((row) => row.isActive), recordType, act ?? null);
  /** 🔒 Decision 11301: one file's line as it stands, its typed name and date, or the name the rule writes for its date and today. */
  const lineOf = useCallback((file: { id: string; name: string }, chosenType: FileTypeAnswer | null) => {
    const draft = lines[file.id];
    const date = draft?.date ?? today;
    const formName = draft?.formName ?? '';
    const name = draft?.name ?? uploadLineName({ typeName: chosenType?.name ?? '', employmentForm: chosenType?.isEmploymentForm === true, entity, date, typedName: file.name, formName });
    const standardForm = chosenType !== null && isStandardForm(chosenType.name, chosenType.isEmploymentForm === true);
    // 🔒 Row T3673: a type whose date comes from its own record (the Tax Exempt Certificate) draws no date box and sends none (decision 11301).
    const recordDated = chosenType !== null && standardForm && !standardFormAsksDate(chosenType.name, chosenType.isEmploymentForm === true);
    const takesName = chosenType !== null && standardFormTakesName(chosenType.name, chosenType.isEmploymentForm === true);
    return { name, date, formName, takesName, recordDated, nameProblem: uploadFileNameProblem(name, file.name), formNameProblem: takesName ? standardFormFieldsProblem({ formName }) : null, dateProblem: recordDated ? null : documentDateProblem({ date, today, standardForm }) };
  }, [lines, today, entity]);
  const setLine = (file: { id: string; name: string }, chosenType: FileTypeAnswer | null, patch: Partial<LineDraft>) =>
    setLines((was) => {
      const now = lineOf(file, chosenType);
      const draft = was[file.id] ?? { name: null, date: now.date, formName: '' };
      return { ...was, [file.id]: { ...draft, ...patch } };
    });
  const asRow = (row: FileTypeAnswer): FileTypeRow => ({ id: row.id, name: row.name, documentClass: row.classification, runsPhotoPipeline: row.runsPhotoPipeline === true });
  const fileTypes: FileTypeRow[] = cutTypes.offered.map(asRow);
  const otherFileTypes: FileTypeRow[] = cutTypes.others.map(asRow);

  const { check: checkSameName, question: sameNameQuestion } = useSameNameQuestion(apiBaseUrl);
  const { check: checkDuplicate, question: duplicateQuestion } = useDuplicateQuestion(apiBaseUrl);

  /** The chosen type rides with each file: the modal holds the queue until one is picked, so a file is never sent without one. */
  const uploadFile = useCallback<UploadFn>(
    async (file, onProgress, signal) => {
        if (!file.blob) throw new Error('The browser handed over no bytes for this file.');
        if (!file.fileTypeId) throw new Error('Pick a file type before the upload starts.');
        const chosenType = typeRows.find((row) => row.id === file.fileTypeId) ?? null;
        // 🔒 Decision 11301: the file's own line, refused before anything is sent while its name or its date is.
        const line = linesFor(chosenType) ? lineOf(file, chosenType) : null;
        if (line && (line.nameProblem || line.formNameProblem || line.dateProblem)) throw new Error(line.nameProblem ?? line.formNameProblem ?? line.dateProblem!);
        // 🔒 Decision 9680: an Employment Agreement is not sent until its questions are answered; the row says so, and is retried once they are.
        const employmentQuestions = employmentQuestionsOf(chosenType);
        const answersProblem = employmentAnswersProblem(employmentQuestions, answers, employmentQuestions.length > 0);
        setAnswersNeeded(answersProblem);
        if (answersProblem) throw new Error(answersProblem);
        const standard = imageStandardOf(typeRows.find((row) => row.id === file.fileTypeId));
        // 🔒 Decision 3014: the same content, or the same name, already filed on the record's company asks before anything is sent.
        const existing = await checkDuplicate(recordType, recordId, line?.name ?? file.name, file.blob, signal);
        if (signal.aborted) throw new Error('The upload was stopped before anything was sent.');
        if (existing) {
          // Use the existing file: one more record it sits on, and no bytes sent.
          const linked = await post<{ fileId: string; linked: boolean }>(fileLinksPath(existing.id), { taggedRecordType: recordType, taggedRecordId: recordId }, apiBaseUrl);
          if (!linked.ok) throw new Error(linked.message);
          onSaved();
          if (onUploaded) await onUploaded({ id: existing.id, filename: existing.filename });
          onProgress(1);
          return;
        }
        // 🔒 Decision 2421: a name already on the record asks new version or separate file before anything is sent.
        const versionOf = await checkSameName(recordType, recordId, line?.name ?? file.name, file.fileTypeId, signal);
        // Stop during the check sends nothing: the row settles as stopped (decision 2422).
        if (signal.aborted) throw new Error('The upload was stopped before anything was sent.');
        // 🔒 Decision 6382 (T2928): a site photo filed as a new file on a Deal is refused before anything is sent until it names a unit; the server refuses it too. A new version and a link ask for none.
        if (!versionOf && units.length === 0 && unitTagRequired({ taggedRecordType: recordType, mimeType: file.blob.type || 'application/octet-stream', standard })) {
          setUnitNeeded(true);
          throw new Error(t('photo.unit.needed'));
        }
        setUnitNeeded(false);
        const form = new FormData();
        form.append('file', file.blob, file.name);
        if (!versionOf) {
          form.append('fileTypeId', file.fileTypeId);
          form.append('taggedRecordType', recordType);
          form.append('taggedRecordId', recordId);
          // 🔒 Decision 5440: a picture filed with a kind is an existing-equipment photo; the web records no position.
          if (photoKind !== '' && file.blob.type.toLowerCase().startsWith('image/')) form.append('photoKind', photoKind);
          if (onDeal && units.length > 0 && file.blob.type.toLowerCase().startsWith('image/')) form.append(UNIT_TAGS_FIELD, JSON.stringify(units));
          // 🔒 Decision 11301: each file's own name and date ride with its first upload; a new version keeps its name (decision 2421).
          if (line) {
            form.append(FILE_NAME_FIELD, line.name.trim());
            if (!line.recordDated && line.date.trim() !== '') form.append(FORM_DATE_FIELD, line.date);
          }
          // 🔒 Decision 9680: the agreement's answers ride beside its date signed.
          if (employmentQuestions.length > 0) form.append(EMPLOYMENT_ANSWERS_FIELD, JSON.stringify(answers));
        }
        onProgress(0.5);
        const answer = await upload<UploadedFile>(versionOf ? `${FILES_PATH}/${versionOf.id}/versions` : FILES_PATH, form, apiBaseUrl, signal);
        if (!answer.ok) throw new Error(answer.message);
        // 🔒 The tile redraws the moment a file lands, not only when the window closes (decision 2422).
        onSaved();
        if (onUploaded) await onUploaded({ id: answer.data.id, filename: answer.data.filename });
        // 🔒 Decision 6380: the checks are drawn the moment the photo lands, while the person can still retake it.
        const photoChecks = answer.data.photoChecks ?? null;
        if (photoChecks) setChecked((was) => [...was.filter((one) => one.id !== answer.data.id), { id: answer.data.id, filename: answer.data.filename, checks: photoChecks, removed: false }]);
        // Detection proposes and never applies: the held file goes to the review screen (piece 9, scan confirm).
        if (answer.data.photoPipelineStatus === 'AwaitingConfirm') onScanConfirm?.({ id: answer.data.id, filename: answer.data.filename });
        onProgress(1);
    },
    [apiBaseUrl, recordType, recordId, onUploaded, onScanConfirm, onSaved, checkSameName, checkDuplicate, photoKind, onDeal, units, typeRows, linesFor, lineOf, answers],
  );

  /** 🔒 Decision 6380: a duplicate is the person's to remove, never the server's; the upload goes to the record's Trash, restorable there. */
  const removeUpload = async (photo: CheckedPhoto) => {
    const outcome = await post(`${FILES_PATH}/${encodeURIComponent(photo.id)}/trash`, {}, apiBaseUrl);
    if (!outcome.ok) return setRefusal(outcome.message);
    setRefusal(null);
    setChecked((was) => was.map((one) => (one.id === photo.id ? { ...one, removed: true } : one)));
    onSaved();
  };
  const kindChoice = photoKinds ? (
    <Select size={Size.Medium}
      label={t('equipment.kind')}
      value={photoKind}
      onChange={setPhotoKind}
      placeholder={t('equipment.kindNone')}
      options={photoKinds.map((kind) => ({ value: kind, label: kind }))}
      dataAttribute={{ 'data-upload-field': 'photoKind' }}
    />
  ) : null;
  // 🔒 T2928: the unit field and the coaching prompts follow the file type chosen: a type carrying no photo standard (a PDF's, say) draws neither. Before a type is chosen the unit box and the Deal's unit buttons are not drawn (the row T2934 item 5, piece 9), as the file is not yet known; the coaching prompts are.
  const photoPart = onDeal ? ({ fileTypeId }: { fileTypeId: string | null }) => {
    const chosen = fileTypeId === null ? null : typeRows.find((row) => row.id === fileTypeId) ?? null;
    if (chosen !== null && imageStandardOf(chosen) === null) return kindChoice;
    return (
    <div className={styles.photo} data-upload-photo="true">
      {kindChoice}
      {chosen !== null ? (
        <span data-upload-field="unitTags">
          <TextInput size={Size.Medium} label={t('photo.unit.label')} value={unitText} onChange={setUnitText} placeholder={t('photo.unit.hint')} name="unitTags" />
        </span>
      ) : null}
      {chosen !== null && unitNeeded && units.length === 0 ? <p className={styles.check} data-upload-unit-needed="true" role="alert">{t('photo.unit.needed')}</p> : null}
      {chosen !== null && knownUnits.length > 0 ? (
        <div className={styles.units} data-upload-known-units="true">
          <span className={styles.unitsLabel}>{t('photo.unit.known')}</span>
          {knownUnits.map((unit) => (
            <Button key={unit} size={Size.Small} color={units.some((one) => one.toLowerCase() === unit.toLowerCase()) ? Color.Navy : Color.Inactive} text={[unit]} actions={[() => setUnitText(units.some((one) => one.toLowerCase() === unit.toLowerCase()) ? units.filter((one) => one.toLowerCase() !== unit.toLowerCase()).join(', ') : [...units, unit].join(', '))]} />
          ))}
        </div>
      ) : null}
      <div data-upload-coaching="true">
        <p className={styles.coachTitle}>{t('photo.coach.title')}</p>
        <ol className={styles.coach}>
          {PHOTO_COACHING_PROMPT_KEYS.map((key) => <li key={key} data-upload-coach={key}>{t(key)}</li>)}
        </ol>
      </div>
      {checked.length > 0 ? (
        <div data-upload-checks="true">
          <p className={styles.coachTitle}>{t('photo.check.title')}</p>
          <ul className={styles.checks}>
            {checked.map((photo) => photo.removed ? (
              <li key={photo.id} className={styles.clear} data-upload-check-removed={photo.id}>{fill(t('photo.check.removed'), { name: photo.filename })}</li>
            ) : photo.checks.checks.length === 0 ? (
              <li key={photo.id} className={styles.clear} data-upload-check-clear={photo.id}>{fill(t('photo.check.clear'), { name: photo.filename })}</li>
            ) : photo.checks.checks.map((check) => (
              <li key={`${photo.id}:${check.kind}`} className={styles.check} data-upload-check={check.kind} data-upload-check-file={photo.id}>
                <span>{fill(t('photo.check.named'), { name: photo.filename, line: photoCheckLine(check, photo.checks) })}</span>
                {check.kind === 'duplicate' || check.kind === 'credential' || check.kind === 'blurred' ? (
                  <Button size={Size.Small} color={Color.Destructive} text={[t('photo.check.remove')]} actions={[() => void removeUpload(photo)]}
                    confirm={{ title: fill(t('photo.check.removeTitle'), { name: photo.filename }), consequence: { body: t('photo.check.removeBody') }, confirmLabel: t('photo.check.removeConfirm') }} />
                ) : null}
              </li>
            )))}
          </ul>
          {refusal ? <p className={styles.check} data-upload-check-refusal="true">{refusal}</p> : null}
        </div>
      ) : null}
    </div>
    );
  } : kindChoice;

  /** 🔒 Decision 9680: the Employment Agreement's questions, drawn once the agreement is picked; its date signed is each file's own date. */
  const formPart = (fileTypeId: string | null) => {
    const chosen = fileTypeId === null ? null : typeRows.find((row) => row.id === fileTypeId) ?? null;
    const asked = employmentQuestionsOf(chosen);
    if (chosen === null || asked.length === 0) return null;
    const asks = (n: number) => asked.some((q) => q.number === n);
    const set = (patch: Partial<EmploymentAnswers>) => setAnswers((was) => ({ ...was, ...patch }));
    return (
      <div data-upload-form-fields="true" data-upload-employment-form={chosen.name}>
        <FormFields>
          {asks(EMPLOYMENT_QUESTION_CONFIDENTIALITY) ? (
            <span data-upload-field="confidentiality">
              <Select size={Size.Small} label={t('upload.employment.confidentiality')} value={yesNoValue(answers.confidentiality)} onChange={(value) => set({ confidentiality: yesNoAnswer(value) })} options={YES_NO_OPTIONS()} placeholder={t('upload.employment.choose')} required hint={t('upload.employment.writesAgreement')} />
            </span>
          ) : null}
          {asks(EMPLOYMENT_QUESTION_NON_SOLICIT) ? (
            <span data-upload-field="nonSolicit">
              <Select size={Size.Small} label={t('upload.employment.nonSolicit')} value={yesNoValue(answers.nonSolicit)} onChange={(value) => set({ nonSolicit: yesNoAnswer(value), ...(value === 'yes' ? {} : { nonSolicitMonths: null }) })} options={YES_NO_OPTIONS()} placeholder={t('upload.employment.choose')} required />
            </span>
          ) : null}
          {asks(EMPLOYMENT_QUESTION_NON_SOLICIT) && answers.nonSolicit === true ? (
            <span data-upload-field="nonSolicitMonths">
              <NumericInput size={Size.Small} kind="number" label={t('upload.employment.nonSolicitMonths')} value={answers.nonSolicitMonths ?? null} onChange={(value) => set({ nonSolicitMonths: value })} name="nonSolicitMonths" required />
            </span>
          ) : null}
          {asks(EMPLOYMENT_QUESTION_RETURN_OR_DESTROY) ? (
            <span data-upload-field="returnOrDestroy">
              <Select size={Size.Small} label={t('upload.employment.returnOrDestroy')} value={yesNoValue(answers.returnOrDestroy)} onChange={(value) => set({ returnOrDestroy: yesNoAnswer(value) })} options={YES_NO_OPTIONS()} placeholder={t('upload.employment.choose')} />
            </span>
          ) : null}
        </FormFields>
        {answersNeeded ? <p className={styles.check} data-upload-employment-needed="true" role="alert">{answersNeeded}</p> : null}
      </div>
    );
  };

  /**
   * 🔒 Decision 11301: one waiting file's line, its name box and its date box, drawn once a type is chosen. A form's
   * name follows its date's year until the person types one; Form Date reads Date Signed on an employment form.
   */
  const lineFields = (file: QueuedFile, fileTypeId: string | null) => {
    if (file.status !== 'queued' && file.status !== 'failed') return null;
    const chosen = fileTypeId === null ? null : typeRows.find((row) => row.id === fileTypeId) ?? null;
    if (chosen === null || !linesFor(chosen)) return null;
    const line = lineOf(file, chosen);
    const employmentForm = chosen.isEmploymentForm === true;
    const dateLabel = employmentForm ? 'upload.dateSigned' : standardFormAsksDate(chosen.name, employmentForm) ? 'upload.formDate' : 'upload.documentDate';
    return (
      <div className={styles.line} data-upload-line={file.id}>
        <span data-upload-field="fileName">
          <TextInput size={Size.Large} label={t('upload.fileName')} value={line.name} onChange={(name) => setLine(file, chosen, { name })} name={FILE_NAME_FIELD} required maxLength={FILE_NAME_MAX_LENGTH} error={line.nameProblem} />
        </span>
        {line.takesName ? (
          <span data-upload-field="formName">
            <TextInput size={Size.Medium} label={t('upload.formName')} value={line.formName} onChange={(formName) => setLine(file, chosen, { formName })} name={FORM_NAME_FIELD} hint={t('upload.formNameHint')} maxLength={FORM_NAME_MAX_LENGTH} error={line.formNameProblem} />
          </span>
        ) : null}
        {line.recordDated ? null : (
          <span data-upload-field="formDate">
            <DatePicker size={Size.Small} label={t(dateLabel)} value={line.date} onChange={(date) => setLine(file, chosen, { date })} name={FORM_DATE_FIELD} required max={today} error={line.dateProblem} />
          </span>
        )}
      </div>
    );
  };
  /** 🔒 Decision 11300: the first refusal among the waiting files' lines holds Upload, and says why. */
  const sendProblemOf = (queueFiles: readonly QueuedFile[], fileTypeId: string | null): string | null => {
    const chosen = fileTypeId === null ? null : typeRows.find((row) => row.id === fileTypeId) ?? null;
    if (!linesFor(chosen)) return null;
    for (const file of queueFiles) {
      if (file.status !== 'queued') continue;
      const line = lineOf(file, chosen);
      if (line.nameProblem || line.formNameProblem || line.dateProblem) return line.nameProblem ?? line.formNameProblem ?? line.dateProblem;
    }
    return null;
  };
  const aboveZone = (state: { fileTypeId: string | null }) => (
    <>
      {formPart(state.fileTypeId)}
      {typeof photoPart === 'function' ? photoPart(state) : photoPart}
    </>
  );

  return (
    <UploadFileModal
      key={`${recordType}:${recordId}`}
      open={open}
      subtitle={t('uploadModal.recordSubtitle')}
      fileTypes={fileTypes}
      otherFileTypes={otherFileTypes}
      recordDeclaredClass={null}
      moduleDefault={null}
      upload={uploadFile}
      onCancel={() => {
        // 🔒 Decision 9640: Cancel starts the next upload clean as Done does (T3448).
        resetForm();
        // Closing redraws the tile too, so a file that landed before Stop is listed (decision 2422).
        onSaved();
        onCancel();
      }}
      onSave={() => {
        resetForm();
        onSaved();
        onCancel();
      }}
      openerId={openerId}
      autoStart
      initialFiles={initialFiles}
      question={duplicateQuestion ?? sameNameQuestion}
      wide={duplicateQuestion !== null}
      aboveZone={aboveZone}
      // 🔒 Decision 11301 (widening 11300): every upload but a photo act's holds its files until Upload, so each file's name and date are the person's first.
      holdQueue={(fileTypeId) => linesFor(fileTypeId === null ? null : typeRows.find((row) => row.id === fileTypeId) ?? null)}
      sendProblem={sendProblemOf}
      {...(perFile ? { rowFields: lineFields } : {})}
    />
  );
}
